import React, { useState } from 'react';
import { Modal } from './Modal';
import { Language } from '@/types';

interface LocationMapButtonProps {
  postalCode: string; // FSA (first 3 characters of postal code)
  language: Language;
  className?: string;
}

// Map of FSA prefixes to approximate area names
const FSA_AREAS: Record<string, { en: string; fr: string }> = {
  // Montreal
  H1: { en: 'Montreal East', fr: 'Montréal Est' },
  H2: { en: 'Montreal Downtown', fr: 'Centre-ville de Montréal' },
  H3: { en: 'Montreal West', fr: 'Montréal Ouest' },
  H4: { en: 'South Shore', fr: 'Rive-Sud' },
  H7: { en: 'Laval', fr: 'Laval' },
  H8: { en: 'Lachine', fr: 'Lachine' },
  H9: { en: 'West Island', fr: 'Ouest de l\'île' },
  // Quebec City
  G1: { en: 'Quebec City', fr: 'Ville de Québec' },
  G2: { en: 'Quebec City Area', fr: 'Région de Québec' },
  // Toronto
  M1: { en: 'Scarborough', fr: 'Scarborough' },
  M2: { en: 'North York', fr: 'North York' },
  M3: { en: 'North York', fr: 'North York' },
  M4: { en: 'Toronto Central', fr: 'Toronto Centre' },
  M5: { en: 'Downtown Toronto', fr: 'Centre-ville de Toronto' },
  M6: { en: 'York', fr: 'York' },
  M9: { en: 'Etobicoke', fr: 'Etobicoke' },
  // Ottawa
  K1: { en: 'Ottawa', fr: 'Ottawa' },
  K2: { en: 'Ottawa West', fr: 'Ottawa Ouest' },
  // Vancouver
  V5: { en: 'Vancouver', fr: 'Vancouver' },
  V6: { en: 'Vancouver Downtown', fr: 'Centre-ville de Vancouver' },
  V7: { en: 'North Vancouver', fr: 'North Vancouver' },
};

const getAreaName = (postalCode: string, language: Language): string => {
  const prefix = postalCode.slice(0, 2).toUpperCase();
  const area = FSA_AREAS[prefix];
  if (area) {
    return area[language];
  }
  // Return generic area based on first letter
  return language === 'en' ? `Area ${postalCode.toUpperCase()}` : `Zone ${postalCode.toUpperCase()}`;
};

export const LocationMapButton: React.FC<LocationMapButtonProps> = ({
  postalCode,
  language,
  className = '',
}) => {
  const [showMap, setShowMap] = useState(false);

  if (!postalCode || postalCode.length < 3) {
    return null;
  }

  const formattedPostalCode = postalCode.toUpperCase();
  const areaName = getAreaName(formattedPostalCode, language);

  // Create Google Maps embed URL - searches for the FSA in Canada
  const mapQuery = encodeURIComponent(`${formattedPostalCode}, Canada`);
  const mapUrl = `https://www.google.com/maps?q=${mapQuery}&output=embed`;
  const externalMapUrl = `https://www.google.com/maps/search/${mapQuery}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowMap(true)}
        className={`inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors ${className}`}
        title={language === 'en' ? 'View on map' : 'Voir sur la carte'}
      >
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span>{areaName}</span>
      </button>

      <Modal
        isOpen={showMap}
        onClose={() => setShowMap(false)}
        title={language === 'en' ? 'Approximate Location' : 'Emplacement approximatif'}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {language === 'en'
              ? `This provider is located in the ${areaName} area (${formattedPostalCode}).`
              : `Ce prestataire se trouve dans la zone ${areaName} (${formattedPostalCode}).`}
          </p>

          <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
            <iframe
              title="Location Map"
              src={mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <a
            href={externalMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            {language === 'en' ? 'Open in Google Maps' : 'Ouvrir dans Google Maps'}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>

          <p className="text-xs text-gray-500 italic">
            {language === 'en'
              ? 'Note: This shows the general area based on postal code, not the exact address.'
              : 'Note: Ceci montre la zone générale basée sur le code postal, pas l\'adresse exacte.'}
          </p>
        </div>
      </Modal>
    </>
  );
};
